import i18next from "i18next";
import { LoggerFactory, withBreadCrumbs } from "lego-gui-lib";
import { CreateView, IndexView, ShowView } from "lego-gui-lib/lib";
import { observer } from "mobx-react-lite";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
  useRouteMatch
} from "react-router";
import { isAllowed } from "../../common/common";
import { CustomerAwareRowButtons } from "../../common/CustomerAwareRowButtons";
import { NestedView } from "../../common/nestedViewComponent";
import { authStoreContext } from "../../core/mainStore";
import { authSchemaHelper } from "../../core/model/authSchemaHelper";
import { ParentRoute } from "../../core/model/parent";
import { GroupAreaCreate } from "./groups/areas/views";
import {
  AreaSplittingGroupCreate,
  AreaSplittingGroupEdit,
  AreaSplittingGroupIndex
} from "./groups/views";
import {
  areaSplittingContext,
  AreaSplittingSchema,
  CreateAreaSplittingSchema,
  customerAreaSplittingContext,
  EditAreaSplittingSchema
} from "./schema";

const log = LoggerFactory.create("AreaSplittingViews");

export const AreaSplittings = {
  url: "/areaSplittings",
  handler: (props: RouteComponentProps<any>) => {
    const title = i18next.t("hns:areaSplitting:domain_plural");
    const baseUrl = props.match.url;

    return withBreadCrumbs(
      { to: baseUrl, children: title },
      <>
        <ParentRoute path={baseUrl + "/:id"} dataSourceName={"areaSplitting"} />
        <Switch>
          <Route exact={true} path={baseUrl} component={Index} />
          <Route
            path={baseUrl + "/:id/groups"}
            render={(props: RouteComponentProps) => {
              const baseUrl = props.match.url;

              return (
                <>
                  <ParentRoute
                    path={baseUrl + "/:id"}
                    dataSourceName={"areaSplittingGroup"}
                  />
                  <Switch>
                    <Route
                      path={baseUrl + "/:id/areas/create"}
                      component={GroupAreaCreate}
                    />
                    <Route
                      path={baseUrl + "/:id/areas/create/:areaId"}
                      component={GroupAreaCreate}
                    />
                    <Route
                      path={baseUrl + "/create"}
                      component={AreaSplittingGroupCreate}
                    />
                    <Route
                      path={baseUrl + "/create/:groupId"}
                      component={AreaSplittingGroupCreate}
                    />
                    <Route
                      path={baseUrl + "/:id"}
                      component={AreaSplittingGroupEdit}
                    />
                  </Switch>
                </>
              );
            }}
          />
          <Route path={baseUrl + "/create/:id"} component={Clone} />
          <Route path={baseUrl + "/create"} component={Create} />
          <Route path={baseUrl + "/:id"} component={Show} />
        </Switch>
      </>
    );
  }
};

export const Index = observer(({ ...rest }: any) => {
  const areaSplitting = useContext(areaSplittingContext);
  const customerAreaSplitting = useContext(customerAreaSplittingContext);

  const auth = useContext(authStoreContext);

  const { i18n } = useTranslation();
  const title = i18n.t("hns:areaSplitting:domain", { count: 2 });

  return (
    <IndexView
      dataSource={
        auth.isAnyCustomerOwner()
          ? customerAreaSplitting
          : areaSplitting.listDataSource
      }
      {...rest}
      title={title}
      toolbarButtons={{ delete: true, export: true }}
      rowButtons={CustomerAwareRowButtons}
      columns={AreaSplittingSchema}
    />
  );
});

export const Create = observer(({ ...rest }: any) => {
  const areaSplitting = useContext(areaSplittingContext);

  const { i18n } = useTranslation();
  const title = i18n.t("common:create", {
    domain: "hns:areaSplitting:domain"
  });

  const match = useRouteMatch();
  const baseUrl = match.url;
  const history = useHistory();

  return withBreadCrumbs(
    { to: baseUrl + "/create", children: title },
    <CreateView
      dataSource={areaSplitting.oneDataSource}
      {...rest}
      title={title}
      initialValues={authSchemaHelper.prepareInitialValuesForAnyCustomerOwnerCreate(
        areaSplitting
      )}
      fields={CreateAreaSplittingSchema}
      id={match.params}
      goBack={() => history.goBack()}
    />
  );
});

export const Show = observer(({ ...rest }) => {
  const areaSplitting = useContext(areaSplittingContext);

  const { i18n } = useTranslation();
  let title = i18n.t("hns:areaSplitting:domain");

  const match = useRouteMatch();
  const baseUrl = match.url;
  const history = useHistory();

  let allowed = false;
  if (areaSplitting.oneDataSource.one) {
    if (isAllowed(areaSplitting)) {
      allowed = true;
      title = i18n.t("common:edit", { domain: "hns:areaSplitting:domain" });
    }
  }

  return withBreadCrumbs(
    { to: baseUrl + "/edit", children: title },
    <NestedView
      showView={
        <ShowView
          dataSource={areaSplitting.oneDataSource}
          hideDeleteButton={!allowed}
          title={title}
          fields={
            allowed
              ? EditAreaSplittingSchema
              : EditAreaSplittingSchema.makeAllReadonly()
          }
          id={match.params}
          preloadData={false}
          onlyView={!allowed}
          goBack={() => history.goBack()}
        />
      }
      indexView={
        // @ts-ignore
        <AreaSplittingGroupIndex match={match} {...rest} isAllowed={allowed} />
      }
    />
  );
});

export const Clone = observer(({ ...rest }: any) => {
  const areaSplitting = useContext(areaSplittingContext);

  const match = useRouteMatch();
  const history = useHistory();
  const { i18n } = useTranslation();
  const title = i18n.t("common:create", {
    domain: "hns:areaSplitting:domain"
  });

  return withBreadCrumbs(
    { to: match.url + "/edit", children: i18n.t("common:copy") },
    <CreateView
      submit={async () => {
        log.debug(`cloning with id ${match.params}`);
        return areaSplitting.oneDataSource.clone();
      }}
      dataSource={areaSplitting.oneDataSource}
      {...rest}
      title={title}
      fields={CreateAreaSplittingSchema}
      id={match.params}
      initialValues={authSchemaHelper.prepareInitialValuesForAnyCustomerOwnerCreate(
        areaSplitting
      )}
      goBack={() => history.goBack()}
    />
  );
});
